/* ==========================================================================
   Harper Reborn — product-page blocks (main-product.liquid)
   HP.money(cents, format)      money formatter driven by the shop's money format
   <hp-countdown>               → see hp-countdown.js (data-hp-countdown* contract)
   <hp-urgency>                 stock bar (variant → stock map rendered by Liquid)
   <hp-shipping-estimate>       order-by / ships / delivered dates in SHOP time
   <hp-discount-code>           click-to-copy code (uses harper-motion's data-hp-copy)
                                and applies it to a non-empty cart via HP.cart
   <hp-together>                "birlikte al" checkbox bundle → /cart/add.js
   [data-hp-btn-price]          price inside the add-to-cart button
   All customer-facing strings arrive from Liquid as data attributes (locales).
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var HP = (window.HP = window.HP || {});

  /* ---------------- money ---------------- */
  function group(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }
  var entityBox = null;
  function decodeEntities(s) {
    if (s.indexOf('&') === -1) return s;
    if (!entityBox) entityBox = doc.createElement('textarea');
    entityBox.innerHTML = s;
    return entityBox.value;
  }
  HP.money = function (cents, format) {
    cents = Math.round(Number(cents) || 0);
    format = decodeEntities(String(format || HP.moneyFormat || '{{amount}}'));
    var val = Math.abs(cents) / 100;
    var sign = cents < 0 ? '-' : '';
    var fixed = val.toFixed(2).split('.');
    var whole = String(Math.round(val));
    var out;
    // Space variants first: their names contain the generic ones as substrings.
    if (/amount_no_decimals_with_space_separator/.test(format)) {
      out = group(whole, ' ');
    } else if (/amount_with_space_separator/.test(format)) {
      out = group(fixed[0], ' ') + ',' + fixed[1];
    } else if (/amount_with_period_and_space_separator/.test(format)) {
      out = group(fixed[0], ' ') + '.' + fixed[1];
    } else if (/amount_no_decimals_with_comma_separator/.test(format)) {
      out = group(whole, '.');
    } else if (/amount_with_comma_separator/.test(format)) {
      out = group(fixed[0], '.') + ',' + fixed[1];
    } else if (/amount_no_decimals/.test(format)) {
      out = group(whole, ',');
    } else if (/amount_with_apostrophe_separator/.test(format)) {
      out = group(fixed[0], "'") + '.' + fixed[1];
    } else {
      out = group(fixed[0], ',') + '.' + fixed[1];
    }
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, sign + out).trim();
  };

  function subscribeSafe(name, fn) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS[name]) {
      return subscribe(PUB_SUB_EVENTS[name], fn);
    }
    return null;
  }

  /** variantChange events carry the publishing section's id; ignore other products on the page. */
  function ownVariant(el, event) {
    var d = event && event.data ? event.data : null;
    if (!d) return null;
    if (d.sectionId && el.dataset.section && d.sectionId !== el.dataset.section) return undefined;
    return d.variant || null;
  }

  function readJson(el, selector) {
    var node = el.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ---------------- <hp-urgency> ---------------- */
  class HpUrgency extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-urg-text]');
      this.fill = this.querySelector('[data-urg-fill]');
      this.template = this.dataset.template || '';
      this.full = Math.max(1, parseFloat(this.dataset.full || '50'));
      this.threshold = parseFloat(this.dataset.threshold || '0') || 0;
      this.stock = readJson(this, '[data-urg-stock]') || {};
      this.render(this.stockOf(this.dataset.variant));
      this.unsub = subscribeSafe('variantChange', (e) => {
        var v = ownVariant(this, e);
        if (v === undefined) return;
        this.render(v && v.available !== false ? this.stockOf(v.id) : null);
      });
    }
    disconnectedCallback() {
      if (this.unsub) this.unsub();
    }
    stockOf(id) {
      if (id === undefined || id === null) return null;
      var n = this.stock[String(id)];
      return typeof n === 'number' ? n : null;
    }
    render(stock) {
      var show = stock !== null && !isNaN(stock) && stock > 0 && (this.threshold <= 0 || stock <= this.threshold);
      if (!show && window.Shopify && window.Shopify.designMode) {
        // Keep the block clickable in the editor with a sample number.
        stock = Math.max(1, Math.min(this.threshold || 5, 5));
        show = true;
      }
      this.hidden = !show;
      if (!show) return;
      var n = Math.round(stock);
      if (this.textEl) {
        this.textEl.innerHTML = this.template.replace(/\[stok\]|\{\{\s*count\s*\}\}/g, '<b>' + n + '</b>');
      }
      if (this.fill) {
        var pct = Math.max(4, Math.min(100, (n / this.full) * 100));
        this.style.setProperty('--hp-urg', pct.toFixed(1) + '%');
      }
    }
  }
  if (!customElements.get('hp-urgency')) customElements.define('hp-urgency', HpUrgency);

  /* ---------------- <hp-shipping-estimate> ----------------
     Dates are computed on the shop's wall clock: Liquid passes the server epoch
     and the shop's UTC offset; the maths runs on UTC getters/setters of a Date
     shifted by that offset, and Intl formats it with timeZone 'UTC'. */
  function isWeekend(d, mode) {
    var day = d.getUTCDay();
    if (mode === 'satsun') return day === 0 || day === 6;
    if (mode === 'sun') return day === 0;
    return false;
  }
  function addDays(d, n, weekend) {
    var out = new Date(d.getTime());
    var left = Math.max(0, Math.round(n));
    while (left > 0) {
      out.setUTCDate(out.getUTCDate() + 1);
      if (isWeekend(out, weekend)) continue;
      left--;
    }
    while (isWeekend(out, weekend)) out.setUTCDate(out.getUTCDate() + 1);
    return out;
  }
  function parseOffset(z) {
    var m = /^([+-])(\d{2}):?(\d{2})$/.exec(String(z || '').trim());
    if (!m) return 0;
    return (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * 60000;
  }
  function formatter(withWeekday) {
    var lang = doc.documentElement.lang || undefined;
    var opts = withWeekday ? { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' } : { day: 'numeric', month: 'long', timeZone: 'UTC' };
    try {
      return new Intl.DateTimeFormat(lang, opts);
    } catch (e) {
      return { format: function (d) { return d.getUTCDate() + '.' + (d.getUTCMonth() + 1); } };
    }
  }
  class HpShippingEstimate extends HTMLElement {
    connectedCallback() {
      this.unsub = subscribeSafe('variantChange', (e) => {
        var v = ownVariant(this, e);
        if (v === undefined) return;
        this.hidden = !(v && v.available !== false && v.requires_shipping !== false);
      });
      this.compute();
    }
    disconnectedCallback() {
      if (this.unsub) this.unsub();
    }
    compute() {
      var weekend = this.dataset.weekend || 'satsun';
      var cutoff = (this.dataset.cutoff || '').trim();
      var serverNow = parseFloat(this.dataset.now || '0') * 1000;
      var offset = parseOffset(this.dataset.offset);
      // Shop wall-clock time expressed as a UTC Date.
      var now = new Date((serverNow || Date.now()) + offset);
      var base = new Date(now.getTime());
      var beforeCutoff = false;
      if (/^\d{1,2}:\d{2}$/.test(cutoff)) {
        var hm = cutoff.split(':');
        var c = new Date(now.getTime());
        c.setUTCHours(parseInt(hm[0], 10), parseInt(hm[1], 10), 0, 0);
        beforeCutoff = now < c;
        if (!beforeCutoff) base.setUTCDate(base.getUTCDate() + 1);
      }
      while (isWeekend(base, weekend)) base.setUTCDate(base.getUTCDate() + 1);
      var ship = addDays(base, parseFloat(this.dataset.ship || '1'), weekend);
      var dMin = addDays(ship, parseFloat(this.dataset.min || '2'), weekend);
      var dMax = addDays(ship, parseFloat(this.dataset.max || '4'), weekend);
      var fmt = formatter(false);
      var fmtW = formatter(true);
      var deliver = dMin.getTime() === dMax.getTime() ? fmt.format(dMin) : fmt.format(dMin) + ' – ' + fmt.format(dMax);

      var el;
      if ((el = this.querySelector('[data-ship-cutoff]'))) {
        el.hidden = !(beforeCutoff && cutoff);
        el.textContent = (this.dataset.strCutoff || '%time%').replace('%time%', cutoff);
      }
      if ((el = this.querySelector('[data-ship-order]'))) el.textContent = fmtW.format(now);
      if ((el = this.querySelector('[data-ship-ship]'))) el.textContent = fmtW.format(ship);
      if ((el = this.querySelector('[data-ship-deliver]'))) el.textContent = deliver;
      this.hidden = this.dataset.available === 'false';
    }
  }
  if (!customElements.get('hp-shipping-estimate')) customElements.define('hp-shipping-estimate', HpShippingEstimate);

  /* ---------------- <hp-discount-code> ----------------
     The copy itself is done by harper-motion's [data-hp-copy] hook; here the
     code is also applied to the cart when the cart has items. */
  class HpDiscountCode extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-hp-copy]');
      this.msg = this.querySelector('[data-hp-code-msg]');
      if (!this.btn) return;
      this.onClick = () => {
        var code = this.btn.dataset.hpCopy || '';
        if (!code || !HP.cart) return;
        this.say('');
        HP.cart
          .fetch()
          .then((c) => {
            if (!c || c.item_count === 0) return null;
            return HP.cart.add(code);
          })
          .then((res) => {
            if (!res) return;
            if (res.ok) this.say(this.dataset.msgApplied || '', true);
            else this.say(res.reason === 'combine' ? this.dataset.msgCombine || this.dataset.msgInvalid || '' : this.dataset.msgInvalid || '');
          })
          .catch(() => this.say(this.dataset.msgError || (window.cartStrings && window.cartStrings.error) || ''));
      };
      this.btn.addEventListener('click', this.onClick);
    }
    disconnectedCallback() {
      if (this.btn && this.onClick) this.btn.removeEventListener('click', this.onClick);
    }
    say(text, ok) {
      if (!this.msg) return;
      this.msg.textContent = text || '';
      this.msg.hidden = !text;
      this.msg.classList.toggle('is-ok', !!ok);
    }
  }
  if (!customElements.get('hp-discount-code')) customElements.define('hp-discount-code', HpDiscountCode);

  /* ---------------- <hp-together> ---------------- */
  class HpTogether extends HTMLElement {
    connectedCallback() {
      this.fmt = this.dataset.moneyFormat || HP.moneyFormat;
      this.section = this.dataset.section;
      this.variants = (this.dataset.variants || '').split(',').filter(Boolean);
      this.totalEl = this.querySelector('[data-together-total]');
      this.btn = this.querySelector('[data-together-submit]');
      this.msg = this.querySelector('[data-together-msg]');
      this.current = this.querySelector('[data-together-current]');
      this.boxes = [].slice.call(this.querySelectorAll('[data-together-item]'));
      this.onChange = () => this.update();
      this.boxes.forEach((b) => b.addEventListener('change', this.onChange));
      if (this.btn) {
        this.onSubmit = (e) => {
          e.preventDefault();
          this.add();
        };
        this.btn.addEventListener('click', this.onSubmit);
      }
      if (this.current) {
        this.unsub = subscribeSafe('variantChange', (e) => {
          var v = ownVariant(this, e);
          if (v === undefined) return;
          // Belt and braces: the variant must belong to this product.
          if (v && this.variants.length && this.variants.indexOf(String(v.id)) === -1) return;
          if (v) {
            this.current.dataset.id = v.id;
            this.current.dataset.price = v.price;
            this.current.disabled = !v.available;
            var p = this.querySelector('[data-together-current-price]');
            if (p) p.textContent = HP.money(v.price, this.fmt);
          } else {
            this.current.disabled = true;
          }
          this.update();
        });
      }
      this.update();
    }
    disconnectedCallback() {
      this.boxes.forEach((b) => b.removeEventListener('change', this.onChange));
      if (this.btn && this.onSubmit) this.btn.removeEventListener('click', this.onSubmit);
      if (this.unsub) this.unsub();
    }
    selected() {
      return this.boxes.filter((b) => b.checked && !b.disabled);
    }
    update() {
      var total = 0;
      this.boxes.forEach((b) => {
        var row = b.closest('[data-together-row]');
        if (row) {
          row.classList.toggle('is-off', !b.checked);
          row.classList.toggle('is-disabled', b.disabled);
        }
        if (b.checked && !b.disabled) total += (parseFloat(b.dataset.price) || 0) * (parseInt(b.dataset.qty || '1', 10) || 1);
      });
      if (this.totalEl) this.totalEl.textContent = HP.money(total, this.fmt);
      if (this.btn) this.btn.disabled = this.selected().length === 0;
    }
    say(text, ok) {
      if (!this.msg) return;
      this.msg.textContent = text || '';
      this.msg.hidden = !text;
      this.msg.classList.toggle('is-ok', !!ok);
    }
    refreshBubble() {
      fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '?section_id=cart-icon-bubble')
        .then((r) => r.text())
        .then((html) => {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          var target = doc.getElementById('cart-icon-bubble');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    }
    add() {
      var items = this.selected().map((b) => ({ id: parseInt(b.dataset.id, 10), quantity: parseInt(b.dataset.qty || '1', 10) || 1 }));
      if (!items.length) return;
      var drawer = doc.querySelector('cart-drawer');
      var body = { items: items };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      var cfg = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
      cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
      cfg.body = JSON.stringify(body);
      this.classList.add('is-busy');
      this.say('');
      fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add', cfg)
        .then((r) => r.json())
        .then((res) => {
          if (res.status) {
            this.say(res.description || res.message || (window.cartStrings && window.cartStrings.error) || this.dataset.msgError || '');
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hp-together', cartData: res });
          }
          if (drawer && typeof drawer.renderContents === 'function') {
            // Drawer: paint and open it with the add response.
            drawer.renderContents(res);
          } else {
            // Notification / page cart types: a multi-item response has no top-level
            // `key`, so Dawn's notification cannot render it — refresh the bubble instead.
            this.refreshBubble();
          }
          this.say(this.dataset.msgAdded || '', true);
        })
        .catch(() => this.say((window.cartStrings && window.cartStrings.error) || this.dataset.msgError || ''))
        .finally(() => this.classList.remove('is-busy'));
    }
  }
  if (!customElements.get('hp-together')) customElements.define('hp-together', HpTogether);

  /* ---------------- price inside the ATC button ---------------- */
  function initBtnPrice() {
    doc.querySelectorAll('[data-hp-btn-price]').forEach(function (span) {
      if (span.dataset.hpBound) return;
      span.dataset.hpBound = '1';
      var fmt = span.dataset.moneyFormat || HP.moneyFormat;
      var unit = parseFloat(span.dataset.price || '0');
      var tierText = '';
      var info = span.closest('product-info');
      var sectionId = info ? info.dataset.section : '';
      function paint() {
        span.textContent = tierText || HP.money(unit, fmt);
      }
      subscribeSafe('variantChange', function (e) {
        var d = e && e.data ? e.data : null;
        if (!d || (d.sectionId && sectionId && d.sectionId !== sectionId)) return;
        var v = d.variant;
        if (v) unit = parseFloat(v.price) || 0;
        span.hidden = !v || v.available === false;
        requestAnimationFrame(paint);
      });
      doc.addEventListener('hp:qb:change', function (e) {
        if (sectionId && e.target && e.target.dataset && e.target.dataset.section && e.target.dataset.section !== sectionId) return;
        var d = e.detail || {};
        tierText = d.totalText || '';
        paint();
      });
      paint();
    });
  }
  initBtnPrice();
  doc.addEventListener('shopify:section:load', initBtnPrice);
})();
